import { TFunction } from 'i18next';
import Task from 'models/tasks/task';
import { areDifferent } from 'utils/array';

export enum TaskRelationship {
  None = 0,
  FollowUp = 1,
}

export enum TaskRelationshipDirection {
  None = 0,
  To = 1,
  From = 2,
}

export class TaskRelation {
  taskTaskId: number;

  taskIdFrom: number;

  taskIdTo: number | null;

  relationship: TaskRelationship;

  taskFrom?: Task;

  taskTo?: Task;

  notifyUserId?: string;

  constructor(fromId: number, toId: number | null, relation: TaskRelationship, notifyUserId?: string) {
    this.taskTaskId = 0;
    this.taskIdFrom = fromId;
    this.taskIdTo = toId;
    this.relationship = relation;
    this.notifyUserId = notifyUserId;
  }

  clone = (): TaskRelation => {
    const c = new TaskRelation(this.taskIdFrom, this.taskIdTo, this.relationship, this.notifyUserId);
    c.taskTaskId = this.taskTaskId;
    c.taskFrom = this.taskFrom;
    c.taskTo = this.taskTo;

    return c;
  };

  swap = (): TaskRelation => {
    if (this.taskIdTo === null) return this.clone();

    const c = new TaskRelation(this.taskIdTo, this.taskIdFrom, this.relationship, this.notifyUserId);
    c.taskTaskId = this.taskTaskId;
    c.taskFrom = this.taskTo;
    c.taskTo = this.taskFrom;

    return c;
  };

  task = (currentTaskId?: number): Task | undefined => {
    if (currentTaskId !== undefined) {
      if (this.taskIdFrom === currentTaskId) return this.taskTo;
      if (this.taskIdTo === currentTaskId) return this.taskFrom;
    }

    return this.taskTo ?? this.taskFrom;
  };

  isUnlinked = (): boolean => {
    return this.taskIdTo === null;
  };

  direction = (currentTaskId: number): TaskRelationshipDirection => {
    if (this.taskIdFrom === currentTaskId) return TaskRelationshipDirection.To;
    if (this.taskIdTo === currentTaskId) return TaskRelationshipDirection.From;

    return TaskRelationshipDirection.None;
  };

  relationText = (currentTaskId: number, t: TFunction<string[]>): string => {
    if (this.isUnlinked()) return t('task:Context.RelationTexts.FollowUpPending');

    const dir = this.direction(currentTaskId);
    switch (dir) {
      case TaskRelationshipDirection.From:
        switch (this.relationship) {
          case TaskRelationship.FollowUp:
            return t('task:Context.RelationTexts.FromFollowUp');
        }
        break;
      case TaskRelationshipDirection.To:
        switch (this.relationship) {
          case TaskRelationship.FollowUp:
            return t('task:Context.RelationTexts.ToFollowUp');
        }
        break;
    }

    return '';
  };
}

export const isSameTaskRelation = (left: TaskRelation, right: TaskRelation): boolean => {
  if (left.taskTaskId > 0 && right.taskTaskId > 0) return left.taskTaskId === right.taskTaskId;

  return (
    left.taskIdFrom === right.taskIdFrom &&
    left.taskIdTo === right.taskIdTo &&
    left.relationship === right.relationship
  );
};

export const isTaskRelationEqual = (left: TaskRelation, right: TaskRelation): boolean => {
  return (
    left.taskTaskId === right.taskTaskId &&
    left.taskIdFrom === right.taskIdFrom &&
    left.taskIdTo === right.taskIdTo &&
    left.relationship === right.relationship &&
    left.notifyUserId === right.notifyUserId
  );
};

export const areTaskRelationsDifferent = (
  left: TaskRelation[] | undefined,
  right: TaskRelation[] | undefined,
): boolean => {
  return areDifferent(left, right, isTaskRelationEqual);
};

export interface ITaskRelationTrace {
  taskTaskId: number;
  taskIdFrom: number;
  taskIdTo: number | null;
  relationship: TaskRelationship;
  notifyUserId?: string;
  taskFromId?: number;
  taskToId?: number;
}

export const toTaskRelationTrace = (relation: TaskRelation | undefined): ITaskRelationTrace | undefined => {
  if (!relation) return undefined;

  return {
    taskTaskId: relation.taskTaskId,
    taskIdFrom: relation.taskIdFrom,
    taskIdTo: relation.taskIdTo,
    relationship: relation.relationship,
    notifyUserId: relation.notifyUserId,
    taskFromId: relation.taskFrom?.taskId,
    taskToId: relation.taskTo?.taskId,
  };
};

export const toTaskRelationsTrace = (relations: TaskRelation[]): ITaskRelationTrace[] => {
  return relations.map((relation) => ({
    taskTaskId: relation.taskTaskId,
    taskIdFrom: relation.taskIdFrom,
    taskIdTo: relation.taskIdTo,
    relationship: relation.relationship,
    notifyUserId: relation.notifyUserId,
    taskFromId: relation.taskFrom?.taskId,
    taskToId: relation.taskTo?.taskId,
  }));
};

export const linkTaskRelationToTask = (
  relations: TaskRelation[],
  relationToLink: TaskRelation,
  linkedTask: Task,
): TaskRelation[] => {
  const linkedRelation = relationToLink.clone();
  linkedRelation.taskIdTo = linkedTask.taskId;
  linkedRelation.taskTo = linkedTask;

  let didUpdateRelation = false;

  const updatedRelations = relations.reduce<TaskRelation[]>((result, relation) => {
    const isMatchingRelation = isSameTaskRelation(relation, relationToLink);
    const isDuplicatePendingRelation =
      relationToLink.isUnlinked() &&
      relation.isUnlinked() &&
      relation.taskIdFrom === relationToLink.taskIdFrom &&
      relation.relationship === relationToLink.relationship;

    if (isMatchingRelation || isDuplicatePendingRelation) {
      if (!didUpdateRelation) {
        result.push(linkedRelation);
        didUpdateRelation = true;
      }

      return result;
    }

    result.push(relation);

    return result;
  }, []);

  return didUpdateRelation ? updatedRelations : [...updatedRelations, linkedRelation];
};

export const applyTaskFollowUpSummary = (targetTask: Task): void => {
  const outgoing = targetTask.taskRelations.filter(
    (relation) => relation.relationship === TaskRelationship.FollowUp && relation.taskIdFrom === targetTask.taskId,
  );

  targetTask.hasFollowUp = outgoing.length > 0;
  targetTask.hasUnlinkedFollowUp = outgoing.some((relation) => relation.isUnlinked());
  targetTask.followUpTotal = outgoing.length;
  targetTask.followUpCompleted = outgoing.filter((relation) => relation.task(targetTask.taskId)?.isCompleted()).length;
};
